import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteAbout, getAbouts } from '../../../../../api/endpoints/cmsSite';
import type { About } from '../../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { AboutFormDialog } from '../AboutFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this
 *  project's pinned TypeScript 4.9.5). Confirmed via local-backend swagger + live curl (see
 *  api/types/cmsSite.ts header) — the only one of the 7 CMS-Site resources with directly
 *  observed live row data (4 real "Hakkımızda/Vizyon/Misyon/Faaliyetlerimiz" rows). About is
 *  scoped to a parent Site via `site_Id`; this page is only ever rendered as a tab inside
 *  `SiteDetailPage` for one specific site, so `siteId` comes in as a fixed prop. */
export default function AboutListPage({ siteId }: { siteId: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<About | null>(null);
  const [deleting, setDeleting] = useState<About | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['cms', 'abouts', siteId],
    queryFn: () => getAbouts(siteId),
  });
  const rows: About[] = useMemo(() => query.data?.abouts ?? [], [query.data]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteAbout(id),
    onSuccess: () => {
      enqueueSnackbar('Hakkımızda kaydı silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'abouts'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Hakkımızda</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Kayıt
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Başlık</TableCell>
              <TableCell>Özet</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı hakkımızda içeriği bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.title ?? '—'}</TableCell>
                <TableCell>{row.summary ?? '—'}</TableCell>
                <TableCell>{row.type ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AboutFormDialog open={createOpen} about={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <AboutFormDialog open={!!editing} about={editing} siteId={siteId} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Hakkımızda kaydını sil"
        description={`"${deleting?.title ?? ''}" kaydını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
